import React, { useEffect, useRef } from 'react';

// Use shades of black and grey for a sophisticated look
const BASE_COLORS = [
  { r: 20, g: 20, b: 20 },  // Almost black
  { r: 50, g: 50, b: 50 },  // Dark grey
  { r: 80, g: 80, b: 80 },  // Medium grey
  { r: 110, g: 110, b: 110 }, // Light grey
  { r: 140, g: 140, b: 140 }, // Lighter grey
];

const ParticleBackground = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 }); 

  // Tuning Constants - "Smooth like Butter"
  const GRID_SPACING = 40; 
  const CONNECT_DISTANCE = GRID_SPACING;
  const INFLUENCE_RADIUS = 200; 
  const GRAVITY_Force = 0.15; // Strength of blackhole
  const DAMPING = 0.95; // High damping = slow, heavy feel
  const STIFFNESS = 0.005; // Soft structural springs
  const HOME_STIFFNESS = 0.02; // Very weak anchoring

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const handleMove = (x, y) => {
        mouseRef.current.x = x;
        mouseRef.current.y = y;
    };

    const onMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const onTouchMove = (e) => {
        if (e.touches.length > 0) handleMove(e.touches[0].clientX, e.touches[0].clientY);
    };

    let particles = [];
    let cols = 0;
    let rows = 0;

    const initParticles = () => {
      particles = [];
      cols = Math.ceil(canvas.width / GRID_SPACING) + 1;
      rows = Math.ceil(canvas.height / GRID_SPACING) + 1;

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
            const originX = i * GRID_SPACING;
            const originY = j * GRID_SPACING;
            
            const baseColor = BASE_COLORS[Math.floor(Math.random() * BASE_COLORS.length)];

            particles.push({
                x: originX,
                y: originY,
                originX: originX,
                originY: originY,
                vx: 0,
                vy: 0,
                size: Math.random() * 2 + 1, 
                length: Math.random() * 8 + 4, 
                baseColor: baseColor,
                // Grid coordinates for neighbor lookup
                col: i,
                row: j
            });
        }
      }
    };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('resize', resize);
    resize();

    let animationFrameId;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      // Physics Step
      particles.forEach((p, index) => {
        let forceX = 0;
        let forceY = 0;

        // 1. Home Force 
        const dxHome = p.originX - p.x;
        const dyHome = p.originY - p.y;
        forceX += dxHome * HOME_STIFFNESS;
        forceY += dyHome * HOME_STIFFNESS;

        // 2. Black Hole Interaction
        const dxMouse = mx - p.x;
        const dyMouse = my - p.y;
        const distMouse = Math.sqrt(dxMouse * dxMouse + dyMouse * dyMouse);

        if (distMouse < INFLUENCE_RADIUS) {
            // "Gravity Well" Physics
            // Force = G * M / r (approx)
            // Prevent division by zero with epsilon
            const dist = Math.max(distMouse, 10); 
            const pull = GRAVITY_Force * (1 - distMouse / INFLUENCE_RADIUS);

            forceX += (dxMouse / dist) * pull * 4; // stronger near center
            forceY += (dyMouse / dist) * pull * 4;
        }

        // 3. Structural Springs
        const neighbors = [
            particles[index + 1],       // Right
            particles[index + rows],    // Down
            particles[index - 1],       // Left
            particles[index - rows]     // Up
        ];

        neighbors.forEach(n => {
            if (n) {
                const dCol = Math.abs(p.col - n.col);
                const dRow = Math.abs(p.row - n.row);
                
                if (dCol + dRow === 1) { // Immediate neighbor
                    const dx = n.x - p.x;
                    const dy = n.y - p.y;
                    const dist = Math.sqrt(dx*dx + dy*dy);
                    
                    if (dist > 0) {
                        const displacement = dist - GRID_SPACING;
                        const springForce = displacement * STIFFNESS;
                        const nx = dx / dist;
                        const ny = dy / dist;
                         
                        forceX += nx * springForce;
                        forceY += ny * springForce;
                    }
                }
            }
        });

        p.vx += forceX;
        p.vy += forceY;
        
        p.vx *= DAMPING;
        p.vy *= DAMPING;
      });

      // Position Update & Render
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        // Calculate Angle
        let angle = 0;
        const dxMouse = mouseRef.current.x - p.x;
        const dyMouse = mouseRef.current.y - p.y;
        const distMouse = Math.sqrt(dxMouse * dxMouse + dyMouse * dyMouse);

        // Point at cursor if nearby (Magnetic effect)
        if (distMouse < INFLUENCE_RADIUS * 1.5) {
             angle = Math.atan2(dyMouse, dxMouse);
        } else {
             // Align with velocity
             angle = Math.atan2(p.vy, p.vx);
             if (Math.abs(p.vx) + Math.abs(p.vy) < 0.1) angle = 0;
        }

        const { r, g, b } = p.baseColor;
        // Fade based on distortion
        const distortion = Math.sqrt( (p.x - p.originX)**2 + (p.y - p.originY)**2 );
        // Increase opacity near the "hole"
        const opacity = 0.4 + Math.min(distortion / 50, 0.5); 

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(angle);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
        ctx.fillRect(-p.length / 2, -p.size / 2, p.length, p.size);
        ctx.restore();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};

export default ParticleBackground;
